import { useState, useCallback, useEffect, useMemo } from "react";
import Analytics from "../../../common/analytics";
import { contentListService } from "../../../application/service/help/content-list";
import {
  createAndSearchRegex,
  createOrSearchRegex,
  isRegex,
  splitQuery,
} from "../../../utils/regexp-util";
import { useDebounce } from "use-debounce";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { HelpContent } from "../../../application/domain/help/help-entity";

export type ConditionType = "and" | "or";

export const useSearch = () => {
  const location = useLocation();
  const history = useHistory();
  const { productId: productIdParam } = useParams<{
    productId: string;
  }>();

  const [result, setResult] = useState<HelpContent[]>();
  const [query, setQuery] = useState<string>(
    new URLSearchParams(location.search).get("query") ?? "",
  );
  const [productId, setProductId] = useState<string | undefined>(
    productIdParam,
  );
  const [tags, setTags] = useState<string[]>(
    new URLSearchParams(location.search)
      .get("tags")
      ?.split(",")
      .filter((tag) => tag) || [],
  );
  const [condition, setCondition] = useState<ConditionType>(
    (new URLSearchParams(location.search).get("condition") as ConditionType) ??
      "and",
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const queryWithCondition = useMemo(() => {
    const queries = splitQuery(query);
    if (queries.some((query) => isRegex(query))) {
      return "";
    }
    if (queries.length > 1) {
      const regexQuery =
        condition === "and"
          ? createAndSearchRegex(queries)
          : createOrSearchRegex(queries);
      return regexQuery;
    }
    return queries.join(" ").trim();
  }, [condition, query]);

  const onChangeQuery = useCallback((searchText: string) => {
    setQuery(searchText);
  }, []);

  const onChangeCondition = useCallback((searchCondition: ConditionType) => {
    setCondition(searchCondition);
  }, []);

  const onChangeProduct = useCallback((productId: string) => {
    setProductId(productId);
  }, []);

  const onChangeTags = useCallback((tags: string[]) => {
    setTags(tags);
  }, []);

  const onSearch = useCallback(
    async (query: string, productId?: string, tagIds?: string[]) => {
      if (!query) return;
      setIsLoading(true);
      const helps = await contentListService.fetchContentList(
        query,
        productId,
        tagIds,
      );
      Analytics.helpContentSearch(query, helps.length);
      setIsLoading(false);
      setResult(helps);
    },
    [],
  );

  const [debouncedQuery] = useDebounce(queryWithCondition, 1000);

  useEffect(() => {
    history.replace({
      pathname: `/help${productId ? `/${productId}` : ""}`,
      search: `?query=${query}&condition=${condition}&tags=${tags.length > 0 ? tags.join(",") : ""}`,
      hash: location.hash || "",
    });
  }, [
    condition,
    debouncedQuery,
    history,
    location.hash,
    productId,
    query,
    tags,
  ]);

  useEffect(() => {
    onSearch(debouncedQuery, productId, tags);
  }, [condition, productId, tags, onSearch, debouncedQuery]);

  return {
    isLoading,
    result,
    query,
    onChangeQuery,
    productId,
    onChangeProduct,
    tags,
    onChangeTags,
    condition,
    onChangeCondition,
  };
};

export const useExtractContext = () => {
  const extractContext = useCallback(
    (query: string, html: string | null): string | undefined => {
      const wordNum = 50;

      const queries = splitQuery(query);

      // HTMLタグを除去
      const text = html?.replace(/<[^>]+>/g, "") ?? "";
      // 検索単語の前後を抽出する
      let matches: string[] = [];
      queries.forEach((query) => {
        const match = text.match(
          new RegExp(`(?:.{0,${wordNum}})(${query})(?:.{0,${wordNum}})`, "g"),
        );
        if (match) {
          matches = matches.concat(match);
        }
      });

      return matches.join("...").slice(0, 100);
    },
    [],
  );
  return extractContext;
};
